import { useParams, Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { PRICE_GUIDES } from '../data/prices';
import { ARTICLES } from '../data/articles';

export default function PriceGuide() {
  const { behandling } = useParams();
  const g = PRICE_GUIDES[behandling];
  const [openFaq, setOpenFaq] = useState(-1);

  if (!g) return <Layout><div className="p-16 text-center">Prisguide ikke funnet.</div></Layout>;

  const related = ARTICLES.filter((a) => a.category === g.category).slice(0, 3);
  const cities = Object.keys(g.ranges).filter((k) => k !== 'typical');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: g.faqs.map((f) => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <Layout>
      <Seo
        title={`Hva koster ${g.shortLabel} i Norge? Priser 2025 | Specialists.no`}
        description={`Se typiske priser for ${g.treatment.toLowerCase()} hos private klinikker i Oslo og Norge. Sammenlign klinikker og finn riktig pris for deg.`}
        canonical={`https://specialists.no/priser/${behandling}`}
        jsonLd={jsonLd}
      />
      <section className="bg-[#0A1F44] text-white py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/50 mb-3">
            <Link to="/" className="hover:text-white">Hjem</Link> / <Link to="/priser" className="hover:text-white">Prisguider</Link> / <span className="text-white">{g.shortLabel}</span>
          </nav>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">Hva koster {g.treatment.toLowerCase()}?</h1>
          <p className="text-white/70 mt-4 text-lg">Typisk prisleie: <span className="text-[#3B82F6] font-semibold">{g.ranges.typical}</span></p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#0A1F44]/10 overflow-hidden mb-10">
            <div className="grid grid-cols-4 text-xs uppercase tracking-wider font-semibold text-[#64748B] bg-[#FAF8F5] px-6 py-4">
              <div>By</div><div>Laveste pris</div><div>Typisk</div><div>Høyeste</div>
            </div>
            {cities.map((city) => {
              const r = g.ranges[city];
              return (
                <div key={city} className="grid grid-cols-4 px-6 py-4 border-t border-[#0A1F44]/5">
                  <div className="font-semibold text-[#0A1F44]">{city}</div>
                  <div className="text-[#334155]">{r.low.toLocaleString('no-NO')} kr</div>
                  <div className="text-[#3B82F6] font-semibold">{r.typical.toLocaleString('no-NO')} kr</div>
                  <div className="text-[#334155]">{r.high.toLocaleString('no-NO')} kr</div>
                </div>
              );
            })}
          </div>

          <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Hva påvirker prisen?</h2>
          <ul className="list-disc list-inside space-y-2 text-[#334155] mb-10">
            {g.factors.map((f, i) => (<li key={i}>{f}</li>))}
          </ul>

          <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Hva er inkludert i prisen?</h2>
          <p className="text-[#334155] mb-10">{g.included}</p>

          <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Er det skjulte kostnader?</h2>
          <p className="text-[#334155] mb-10">{g.hidden}</p>

          <div className="bg-white rounded-2xl border-2 border-[#3B82F6] p-8 mb-12">
            <h3 className="font-display text-2xl font-bold text-[#0A1F44] mb-2">Finn klinikk innen din priskategori</h3>
            <p className="text-[#64748B] mb-5">Vi kobler deg med klinikker med 4+ stjerner — svar innen 24 timer.</p>
            <Link to="/#lead-form" className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-3 rounded-full font-semibold">Send forespørsel <ArrowRight size={16} /></Link>
          </div>

          <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Ofte stilte spørsmål</h2>
          <div className="space-y-2 mb-12">
            {g.faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#0A1F44]/10 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left">
                  <span className="font-semibold text-[#0A1F44]">{f.q}</span>
                  <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-[#334155] text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>

          {related.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-4">Relaterte artikler</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((a) => (
                  <Link key={a.slug} to={`/artikler/${a.slug}`} className="bg-white rounded-xl border border-[#0A1F44]/10 p-5 hover:-translate-y-0.5 transition-transform">
                    <div className="text-[10px] uppercase tracking-wider text-[#3B82F6] font-semibold mb-2">{a.readingTime} min lesing</div>
                    <div className="font-display text-lg font-bold text-[#0A1F44] leading-tight">{a.title}</div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
